import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getTopAchieversGuests,
  saveTopAchieversGuests,
} from "../../actions/studensts";
import useTopAchieversLive from "../../utils/useTopAchieversLive";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import TablePagination from "../../components/TablePagination";
import { excelTopAchieversGuests } from "../exportToExcel";

// Four guest slots are shown for every student; the "+" adds one more, up to
// nine. These two numbers are mirrored by the server, which caps the stored
// list at MAX_GUESTS.
const DEFAULT_GUESTS = 4;
const MAX_GUESTS = 9;

const EVENT_LOCATIONS = ["Cairo", "Alexandria"];

const PAGE_SIZE = 100;

// Pads a saved guest list out to the number of visible slots so the row always
// renders at least four inputs.
const toSlots = (guests) => {
  const saved = Array.isArray(guests) ? guests : [];
  const size = Math.max(DEFAULT_GUESTS, saved.length);
  return Array.from({ length: size }, (_, i) => saved[i] || "");
};

function GuestRow({ student, onSaved, onError }) {
  const [slots, setSlots] = useState(toSlots(student.guests));
  const [saving, setSaving] = useState(false);
  // Compared against the saved baseline so the Save button only lights up when
  // there is something to save.
  const [saved, setSaved] = useState(toSlots(student.guests));

  const [eventLocation, setEventLocation] = useState(student.event_location || "");
  const [savedEventLocation, setSavedEventLocation] = useState(student.event_location || "");

  const dirty = useMemo(
    () =>
      slots.filter((n) => n.trim() !== "").join(" ") !==
      saved.filter((n) => n.trim() !== "").join(" ") ||
      eventLocation !== savedEventLocation,
    [slots, saved, eventLocation, savedEventLocation]
  );

  // Another user's save (live update, or this row remounting from the page's
  // list) replaces what's shown — unless this user has unsaved edits there, in
  // which case their edits stay on screen and the old baseline is kept, so
  // their Save still only applies their own additions/removals.
  const guestsKey = JSON.stringify(student.guests || []);
  useEffect(() => {
    const incoming = toSlots(student.guests);
    const clean = (list) => list.filter((n) => n.trim() !== "").join(" ");
    if (clean(slots) === clean(saved)) {
      setSlots(incoming);
      setSaved(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestsKey]);

  useEffect(() => {
    const incoming = student.event_location || "";
    if (eventLocation === savedEventLocation) {
      setEventLocation(incoming);
      setSavedEventLocation(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student.event_location]);

  const handleChange = (index, value) => {
    setSlots((prev) => prev.map((n, i) => (i === index ? value : n)));
  };

  const handleAdd = () => {
    setSlots((prev) => (prev.length >= MAX_GUESTS ? prev : [...prev, ""]));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    // Send the list this screen started from alongside the edited one, so the
    // server applies only this user's additions/removals and keeps guests
    // another user saved meanwhile. The location is only sent when changed here,
    // so an untouched dropdown can't overwrite someone else's choice.
    const res = await saveTopAchieversGuests(
      student.student_id,
      slots,
      eventLocation !== savedEventLocation ? eventLocation : undefined,
      saved
    );
    setSaving(false);
    if (res && res.status === 200) {
      const stored = toSlots(res.data?.guests);
      setSlots(stored);
      setSaved(stored);
      const storedLocation = res.data?.event_location || "";
      setEventLocation(storedLocation);
      setSavedEventLocation(storedLocation);
      onSaved(student, res.data?.guests || [], res.data?.event_location || null);
    } else {
      onError(res?.data?.ERROR || "Failed to save guests.");
    }
  };

  const filled = slots.filter((n) => n.trim() !== "").length;

  return (
    <tr className="align-top border-b border-gray-200">
      <td className="px-4 py-3 border border-gray-200 whitespace-nowrap">
        {student.student_id}
      </td>
      <td className="px-4 py-3 border border-gray-200">{student.full_name}</td>
      <td className="px-4 py-3 border border-gray-200 whitespace-nowrap">
        {student.phone}
      </td>
      <td className="px-4 py-3 border border-gray-200 whitespace-nowrap">
        <select
          value={eventLocation}
          onChange={(e) => setEventLocation(e.target.value)}
          className="border border-gray-300 rounded-lg p-2 text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">Select Location</option>
          {EVENT_LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>
              {loc}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {slots.map((name, i) => (
            <input
              key={i}
              type="text"
              value={name}
              placeholder={`Guest ${i + 1}`}
              onChange={(e) => handleChange(i, e.target.value)}
              className="border border-gray-300 rounded-lg p-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          ))}
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            onClick={handleAdd}
            disabled={slots.length >= MAX_GUESTS}
            title={
              slots.length >= MAX_GUESTS
                ? `Maximum ${MAX_GUESTS} guests per student`
                : "Add another guest"
            }
            className="w-7 h-7 rounded-full bg-blue-800 text-white text-lg leading-none
                       flex items-center justify-center hover:bg-blue-700
                       disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            +
          </button>
          <span className="text-xs text-gray-500">
            {filled} of {slots.length} filled (max {MAX_GUESTS})
          </span>
        </div>
      </td>
      <td className="px-4 py-3 border border-gray-200 text-center">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium
                     hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </td>
    </tr>
  );
}

export default function TopAchieversGuests() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flashSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };
  const flashError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 3500);
  };

  // Rows copy their student's data into local state only when they mount, so
  // the list here must hold what was saved — otherwise searching or paging
  // remounts the row from the stale list and it shows up empty again.
  const handleSaved = (student, guests, event_location) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.student_id === student.student_id ? { ...s, guests, event_location } : s
      )
    );
    flashSuccess(`Guests saved for ${student.full_name}.`);
  };

  // Other users' saves, polled from the server; students not in this list
  // (e.g. marked Top Achiever after the page loaded) appear on reload.
  useTopAchieversLive((changes) => {
    const byId = new Map(changes.map((c) => [c.student_id, c]));
    setStudents((prev) =>
      prev.map((s) => {
        const c = byId.get(s.student_id);
        return c ? { ...s, guests: c.guests, event_location: c.event_location } : s;
      })
    );
  });

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    const res = await getTopAchieversGuests();
    if (res && res.status === 200) {
      setStudents(res.data?.data || []);
    } else {
      flashError(res?.data?.ERROR || "Failed to load Top Achievers.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Always exports every student and guest, not just the current search
  // results, and re-reads from the server so unsaved edits are never exported.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await excelTopAchieversGuests();
    } catch {
      flashError("Failed to export the guests sheet.");
    }
    setExporting(false);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;
    return students.filter(
      (s) =>
        String(s.student_id || "").toLowerCase().includes(term) ||
        String(s.full_name || "").toLowerCase().includes(term) ||
        String(s.phone || "").toLowerCase().includes(term)
    );
  }, [students, search]);

  // Searching narrows the list, so a page number from the old result set can
  // land past the end — snap back to the first page whenever the term changes.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  return (
    <div className="px-4">
      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Top Achievers Guests
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Students who paid the Top Achievers fee. Record up to {MAX_GUESTS}{" "}
            guest names per student.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-semibold">
            {filtered.length} Students
          </span>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium
                       hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {exporting ? "Exporting..." : "Export Sheet"}
          </button>
          <input
            type="text"
            placeholder="Search by ID, name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg p-2 text-sm w-64
                       focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {loading ? (
        <p className="p-4 text-gray-500">Loading...</p>
      ) : filtered.length === 0 ? (
        <h1
          className="w-full my-2 border py-2.5 px-5 rounded-lg bg-gray-50
                     flex items-center justify-between text-sm font-medium text-red-600"
        >
          No Top Achievers Available...
        </h1>
      ) : (
        <div className="overflow-x-auto border rounded-lg shadow bg-white">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-4 py-2 border border-gray-200">National ID</th>
                <th className="px-4 py-2 border border-gray-200">Full Name</th>
                <th className="px-4 py-2 border border-gray-200">Phone</th>
                <th className="px-4 py-2 border border-gray-200">Event Location</th>
                <th className="px-4 py-2 border border-gray-200">Guests Names</th>
                <th className="px-4 py-2 border border-gray-200 text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {paged.map((student) => (
                <GuestRow
                  key={student.student_id}
                  student={student}
                  onSaved={handleSaved}
                  onError={flashError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination
        page={page}
        setPage={setPage}
        totalItems={filtered.length}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
}
